import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { fetchExchangeRate } from '../util/api';
import {
  DISPLAY_CURRENCY_EUR,
  DISPLAY_CURRENCY_STORAGE_KEY,
  DISPLAY_CURRENCY_USD,
  getDefaultDisplayCurrency,
  isValidDisplayCurrency,
} from '../util/displayCurrency';

const DisplayCurrencyContext = createContext({
  displayCurrency: DISPLAY_CURRENCY_EUR,
  setDisplayCurrency: () => {},
  eurToUsdRate: null,
  eurToUsdRateDate: null,
  eurToUsdRateSource: null,
  fxReady: false,
});

export { DisplayCurrencyContext };

const readStoredDisplayCurrency = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(DISPLAY_CURRENCY_STORAGE_KEY);
    return isValidDisplayCurrency(stored) ? stored : null;
  } catch (e) {
    return null;
  }
};

const writeStoredDisplayCurrency = value => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(DISPLAY_CURRENCY_STORAGE_KEY, value);
  } catch (e) {
    // Ignore quota / private mode errors
  }
};

/**
 * Provides buyer display currency preference (EUR|USD) and EUR→USD FX rate for estimates.
 */
export const DisplayCurrencyProvider = props => {
  const { children } = props;
  const [displayCurrency, setDisplayCurrencyState] = useState(DISPLAY_CURRENCY_EUR);
  const [hydrated, setHydrated] = useState(false);
  const [eurToUsdRate, setEurToUsdRate] = useState(null);
  const [eurToUsdRateDate, setEurToUsdRateDate] = useState(null);
  const [eurToUsdRateSource, setEurToUsdRateSource] = useState(null);
  const [fxReady, setFxReady] = useState(false);

  // Hydrate preference from localStorage on the client only (SSR and first visit default to EUR).
  useEffect(() => {
    const stored = readStoredDisplayCurrency();
    const initial = stored || getDefaultDisplayCurrency();
    setDisplayCurrencyState(initial);
    if (!stored) {
      writeStoredDisplayCurrency(initial);
    }
    setHydrated(true);
  }, []);

  const setDisplayCurrency = useCallback(next => {
    if (!isValidDisplayCurrency(next)) {
      return;
    }
    setDisplayCurrencyState(next);
    writeStoredDisplayCurrency(next);
  }, []);

  // Prefetch EUR→USD whenever the user may need USD estimates.
  useEffect(() => {
    if (!hydrated) {
      return;
    }
    let cancelled = false;

    const loadRate = () => {
      fetchExchangeRate({ from: DISPLAY_CURRENCY_EUR, to: DISPLAY_CURRENCY_USD })
        .then(fx => {
          if (cancelled) {
            return;
          }
          if (Number.isFinite(fx?.rate) && fx.rate > 0) {
            setEurToUsdRate(fx.rate);
            setEurToUsdRateDate(fx.date || null);
            setEurToUsdRateSource(fx.source || null);
          }
        })
        .catch(() => {
          // Keep previous rate if any; browse falls back to EUR when rate missing
        })
        .finally(() => {
          if (!cancelled) {
            setFxReady(true);
          }
        });
    };

    // Always load once hydrated so switching to USD is instant; cheap + cached server-side.
    loadRate();

    return () => {
      cancelled = true;
    };
  }, [hydrated]);

  const value = useMemo(
    () => ({
      displayCurrency,
      setDisplayCurrency,
      eurToUsdRate,
      eurToUsdRateDate,
      eurToUsdRateSource,
      fxReady,
    }),
    [
      displayCurrency,
      setDisplayCurrency,
      eurToUsdRate,
      eurToUsdRateDate,
      eurToUsdRateSource,
      fxReady,
    ]
  );

  return (
    <DisplayCurrencyContext.Provider value={value}>{children}</DisplayCurrencyContext.Provider>
  );
};

export const useDisplayCurrency = () => useContext(DisplayCurrencyContext);
